
import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { MapPinIcon } from "lucide-react";
import { Constants } from "@/integrations/supabase/types";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import EvidenceUploader from "./EvidenceUploader";
import UploadProgress from "./UploadProgress";

const formSchema = z.object({
  category: z.enum(Constants.public.Enums.crime_category),
  description: z.string().min(10, "Description must be at least 10 characters").max(500, "Description must be less than 500 characters"),
  latitude: z.number(),
  longitude: z.number(),
  locationName: z.string().optional(),
});

export type CrimeFormValues = z.infer<typeof formSchema>;

interface CrimeReportFormProps {
  onSubmit: (data: CrimeFormValues) => void;
  isSubmitting: boolean;
  uploadProgress: number;
  currentLocation: {
    lat: number;
    lng: number;
    name?: string;
  } | null;
  imageFiles: File[];
  videoFiles: File[];
  onAddImages: (files: File[]) => void;
  onRemoveImage: (index: number) => void;
  onAddVideos: (files: File[]) => void;
  onRemoveVideo: (index: number) => void;
}

const CrimeReportForm: React.FC<CrimeReportFormProps> = ({
  onSubmit,
  isSubmitting,
  uploadProgress,
  currentLocation,
  imageFiles,
  videoFiles,
  onAddImages,
  onRemoveImage,
  onAddVideos,
  onRemoveVideo,
}) => {
  const form = useForm<CrimeFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      category: undefined,
      description: "",
      latitude: currentLocation?.lat || 0,
      longitude: currentLocation?.lng || 0,
      locationName: currentLocation?.name || "",
    },
  });

  // Update form values when location changes
  React.useEffect(() => {
    if (currentLocation) {
      form.setValue("latitude", currentLocation.lat);
      form.setValue("longitude", currentLocation.lng);
      form.setValue("locationName", currentLocation.name || "");
    }
  }, [currentLocation, form]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="category"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Crime Category</FormLabel>
              <Select 
                onValueChange={field.onChange} 
                defaultValue={field.value}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {Constants.public.Enums.crime_category.map((category) => (
                    <SelectItem key={category} value={category}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Provide details about the incident"
                  className="resize-none"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <EvidenceUploader
          imageFiles={imageFiles}
          videoFiles={videoFiles}
          onAddImages={onAddImages}
          onRemoveImage={onRemoveImage}
          onAddVideos={onAddVideos}
          onRemoveVideo={onRemoveVideo}
        />

        <div className="flex items-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md text-sm text-muted-foreground border border-blue-100 dark:border-blue-800">
          <MapPinIcon className="h-4 w-4 mr-2 text-blue-500" />
          {currentLocation 
            ? (
              <div>
                <p className="font-medium text-blue-700 dark:text-blue-300">Location selected</p>
                <p className="text-xs text-blue-600 dark:text-blue-400">
                  {currentLocation.name || `Coordinates: (${currentLocation.lat.toFixed(4)}, ${currentLocation.lng.toFixed(4)})`}
                </p>
                <p className="text-xs mt-1 text-blue-500 dark:text-blue-400">
                  You can adjust the location on the map
                </p>
              </div>
            ) 
            : "No location selected. Please select a location on the map."}
        </div>

        <UploadProgress isSubmitting={isSubmitting} progress={uploadProgress} />

        <Button 
          type="submit" 
          className="w-full" 
          disabled={isSubmitting || !currentLocation}
        >
          {isSubmitting ? "Submitting..." : "Submit Report"}
        </Button>
      </form>
    </Form>
  );
};

export default CrimeReportForm;
